import { useState } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
import {
  Button, Card, Chip, DataTable, Pane, PaneHead, PhoneScreen,
} from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { PeriodRow } from './components/PeriodRow';
import { PhonePeriod } from './components/PhonePeriod';
import { exportXlsx } from './lib/exportXlsx';
import {
  amount, money, monthName, periodLabel, thisPeriod, useTaxReport, type TaxReport,
} from './lib/useAccounts';
import { AccountantMenu } from './AccountantMenu';

/**
 * Tax report — `g_tax` (10). THE EXEMPT / TAXABLE SPLIT.
 *
 * A CLINIC'S MEDICAL WORK IS EXEMPT AND ITS PRODUCTS ARE NOT, and the two sit
 * on the same bills. Separating them is the entire job of this screen, and the
 * split is read from each invoice's own tax rate rather than from a category
 * guess: a line billed at zero IS exempt, whatever anybody later calls it.
 *
 * VAT FOLLOWS THE MONEY. It is prorated to what was actually collected,
 * because a half-paid invoice has not yet produced the whole of its tax.
 */
export function TaxPage() {
  const [period, setPeriod] = useState(thisPeriod);
  const { data, isLoading, isError } = useTaxReport(period);

  usePageMeta({ title: 'Tax report' });
  const [query, setQuery] = useState('');
  const [compare, setCompare] = useState(false);

  const [y, m] = period.split('-').map(Number);
  const months = [2, 1, 0].map((by) => {
    const d = new Date(y, m - 1 - by, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const trio = useQueries({
    queries: months.map((p) => ({
      queryKey: ['accounts', 'tax', p],
      staleTime: 60_000,
      retry: false,
      queryFn: async (): Promise<TaxReport | null> => {
        const res = await apiClient.get('/accounts/tax', { params: { period: p } });
        return ((res.data as { data?: TaxReport })?.data ?? res.data) as TaxReport;
      },
    })),
  });
  /* The clinic's tax registration is set by the manager in Money; the accountant
     reads just that slice of it from the books' own endpoint. */
  const policy = useQuery({
    queryKey: ['accountant', 'money-policy'],
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () => {
      const res = await apiClient.get('/accounts/tax-registration');
      return ((res.data as { data?: { taxId?: string | null; vatPercent?: number | null; etaStatus?: string } })?.data ?? res.data) as {
        taxId?: string | null;
        vatPercent?: number | null;
        etaStatus?: 'not_started' | 'configured' | 'live';
      };
    },
  });


  /* `g_a2`'s "Sequential bill range" — first to last number issued in the
     month, and whether any number in between is missing. A deleted bill keeps
     its number, so it is named as deleted rather than counted as a hole. */
  const range = data?.billRange;
  const rangeCard = (compact: boolean) => range?.first ? (
    <div className="card">
      <p className="t-ovl mut">Sequential bill range</p>
      <p className="t-strong mt4">
        {range.first} → {range.last} ·{' '}
        {range.missing
          ? <span className="red">{range.missing} missing</span>
          : <span className="grn">no gaps ✓</span>}
      </p>
      {compact ? null : (
        <p className="t-cap mut mt4">
          {range.issued} bill{range.issued === 1 ? '' : 's'} issued
          {range.deleted ? ` · ${range.deleted} deleted after issue` : ''}
        </p>
      )}
    </div>
  ) : null;

  /* FOUR ROWS: three classes, and what was refunded or voided on the taxable
     ones — shown negative, because a refund is a row, never a missing number. */
  const refundRow = data?.refundCount
    ? [{
        key: 'refunds',
        label: 'Refunds & voids (taxable)',
        sub: `${data.refundCount} logged with a reason`,
        ratePercent: data.taxableBaseMinor && data.vatMinor ? Math.round((data.vatMinor / data.taxableBaseMinor) * 100) : 0,
        baseMinor: -data.refundedBaseMinor,
        vatMinor: -data.refundedVatMinor,
        refund: true,
      }]
    : [];
  const rows: TableRow[] = [
    ...(data?.classes ?? []).map((c) => ({ ...c, refund: false })),
    ...refundRow,
  ]
    .filter((c) => !query.trim() || c.label.toLowerCase().includes(query.trim().toLowerCase()))
    .map((c) => ({
    id: c.key,
    cells: {
      cls: (
        <div style={{ minWidth: 0 }}>
          <p className={`t-strong${c.refund ? ' red' : ''}`}>{c.label}</p>
          <p className="t-cap mut">{c.sub}</p>
        </div>
      ),
      base: <span className={`t-strong num${c.refund ? ' red' : ''}`}>{c.baseMinor < 0 ? '−' : ''}{amount(Math.abs(c.baseMinor))}</span>,
      rate: <span className="mut num">{c.ratePercent ? `${c.ratePercent}%` : '—'}</span>,
      vat: <span className={`t-strong num${c.refund ? ' red' : ''}`}>{c.vatMinor < 0 ? '−' : ''}{c.vatMinor || c.refund ? amount(Math.abs(c.vatMinor)) : '—'}</span>,
    },
  }));

  useShellChrome(
    {
      raw: true,
      /* `g_tax` — the desk table as cards, over the table's own rows, so a
         figure can never read differently here. */
      phone: (
        <PhoneScreen
          className="acc-phone"
          crumb="Accounts"
          title={`Tax · ${periodLabel(period)}`}
          menu={(close) => <AccountantMenu door="tax" onClose={close} />}
        >
          <PhonePeriod period={period} onChange={setPeriod} />
          {/* `g_a2` — the four lines of the return, one card. */}
          <div className="card">
            {[
              ['Exempt base — medical services', data?.exemptBaseMinor ?? 0],
              ['Taxable base — products & cosmetic', data?.taxableBaseMinor ?? 0],
              ['VAT on taxable', data?.vatMinor ?? 0],
            ].map(([label, v]) => (
              <div key={label as string} className="flex" style={{ justifyContent: 'space-between', gap: 12, padding: '4px 0' }}>
                <span className="t-body">{label}</span>
                <span className="t-strong num">{amount(v as number)}</span>
              </div>
            ))}
            <div className="flex" style={{ justifyContent: 'space-between', gap: 12, padding: '4px 0' }}>
              <span className="t-body">Refunded VAT</span>
              <span className={`t-strong num${data?.refundedVatMinor ? ' red' : ''}`}>
                {data?.refundedVatMinor ? `− ${amount(data.refundedVatMinor)}` : amount(0)}
              </span>
            </div>
          </div>
          {rangeCard(true)}
          <Card kind="accent">
            <p className="t-sec brand">
              {policy.data?.etaStatus === 'live'
                ? 'ETA e-invoice: live — bills are sent to the tax authority.'
                : policy.data?.etaStatus === 'configured'
                  ? 'ETA e-invoice: fields configured, export formatted for the portal when the mandate lands.'
                  : policy.isError || policy.isLoading ? 'ETA e-invoice: status unavailable right now.'
                  : 'ETA e-invoice: not started — the clinic manager sets it in Settings ▸ Money.'}
            </p>
          </Card>
          <button type="button" className="btn btn-primary mt12" disabled={!data?.classes.length}
            onClick={() => exportTax()}>
            Export tax report (.xlsx)
          </button>
        </PhoneScreen>
      ),
      title: 'Tax report',
      sub: (
        <>
          exempt / taxable split <Chip kind="outline" small>read-only</Chip>
        </>
      ),
      search: 'Search a tax class…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export (.xlsx)',
        disabled: !data?.classes.length,
        onClick: () => exportTax(),
      },
      pane: (
        <Pane
          head={<PaneHead overline="This report" title="Filing pack" sub="its own export" />}
          foot={
            <Button kind="primary" style={{ width: '100%' }} disabled={!data?.classes.length} onClick={() => exportTax()}>
              Export filing pack
            </Button>
          }
        >
          <Card>
            <p className="t-ovl mut">Tax registration</p>
            <p className="t-sec sec mt4">{policy.isError ? 'unavailable' : policy.isLoading ? '…' : policy.data?.taxId || 'not set'}</p>
            <p className="t-cap mut mt8">from Settings ▸ Money</p>
          </Card>
          <Card>
            <p className="t-ovl mut">ETA e-invoice</p>
            <p className="t-sec sec mt4">
              {policy.data?.etaStatus === 'live'
                ? 'live — bills are sent to the tax authority'
                : policy.data?.etaStatus === 'configured'
                  ? 'configured — activity and branch codes are set; export is formatted for the portal'
                  : policy.isError ? 'unavailable' : policy.isLoading ? '…' : 'not started'}
            </p>
            <p className="t-cap mut mt8">set by the clinic manager in Settings ▸ Money</p>
          </Card>
          <Card>
            <p className="t-ovl mut">Month on month</p>
            <p className="t-sec sec mt4">
              {months.map((p, i) => (
                <span key={p}>
                  {i ? ' · ' : ''}
                  {i === 2 ? <b>{periodLabel(p).split(' ')[0]} {amount(trio[i]?.data?.vatMinor ?? 0)}</b> : `${periodLabel(p).split(' ')[0]} ${amount(trio[i]?.data?.vatMinor ?? 0)}`}
                </span>
              ))}
            </p>
            <p className="t-cap mut mt8">VAT collected, prorated to what was paid</p>
          </Card>
        </Pane>
      ),
    },
    [data, period, query, policy.data, trio[0].data, trio[1].data, trio[2].data],
  );

  /* THE FILING PACK: the split, the refunds beneath it and the bill range, as
     one workbook the return can be keyed from. */
  function exportTax() {
    const classes = (data?.classes ?? []).map((c) => [
      c.label,
      (c.baseMinor / 100).toFixed(2),
      `${c.ratePercent}%`,
      (c.vatMinor / 100).toFixed(2),
      String(c.bills),
    ]);
    if (data?.refundCount) {
      classes.push([
        'Refunds & voids (taxable)',
        (-data.refundedBaseMinor / 100).toFixed(2),
        '',
        (-data.refundedVatMinor / 100).toFixed(2),
        String(data.refundCount),
      ]);
    }
    const r = data?.billRange;
    void exportXlsx(`tax-${period}`, [
      { name: 'Tax split', headers: ['Tax class', 'Base', 'Rate', 'VAT', 'Bills'], rows: classes },
      ...(r?.first
        ? [{
            name: 'Bill range',
            headers: ['First', 'Last', 'Issued', 'Missing', 'Deleted after issue'],
            rows: [[r.first, r.last ?? '', String(r.issued), String(r.missing), String(r.deleted)]],
          }]
        : []),
    ]);
  }

  return (
    <div className="maie desk acc-root">
      <PeriodRow
        period={period}
        onChange={setPeriod}
        branches={data?.branches}
        trailing={
          <>
            <span className="t-cap mut" style={{ padding: '0 4px' }}>·</span>
            <Chip kind={compare ? 'brand' : 'outline'} small onClick={() => setCompare(!compare)}>
              Compare {periodLabel(months[1]).split(' ')[0]}
            </Chip>
          </>
        }
      />

      <div className="dcontent scroll">
        <div className="acc-stats">
          <div className="card" style={{ margin: 0 }}>
            <p className="t-ovl mut">Exempt base</p>
            <p className="t-dxl mt8">{money(data?.exemptBaseMinor ?? 0)}</p>
            <p className="t-cap mut mt4">
              {compare ? `${periodLabel(months[1]).split(' ')[0]} ${amount(trio[1]?.data?.exemptBaseMinor ?? 0)}` : 'medical work — no VAT due on it'}
            </p>
          </div>
          <div className="card" style={{ margin: 0 }}>
            <p className="t-ovl mut">Taxable base</p>
            <p className="t-dxl mt8">{money(data?.taxableBaseMinor ?? 0)}</p>
            {compare ? <p className="t-cap mut mt4">{periodLabel(months[1]).split(' ')[0]} {amount(trio[1]?.data?.taxableBaseMinor ?? 0)}</p> : null}
          </div>
          <div className="card" style={{ margin: 0 }}>
            <p className="t-ovl mut">VAT collected</p>
            <p className="t-dxl mt8">{money(data?.vatMinor ?? 0)}</p>
            <p className="t-cap mut mt4">
              {compare ? `${periodLabel(months[1]).split(' ')[0]} ${amount(trio[1]?.data?.vatMinor ?? 0)}` : 'prorated to what was actually paid'}
            </p>
          </div>
          <div className="card" style={{ margin: 0 }}>
            <p className="t-ovl mut">Refunded VAT</p>
            <p className={`t-dxl mt8${data?.refundedVatMinor ? ' amb' : ''}`}>
              {money(data?.refundedVatMinor ?? 0)}
            </p>
            <p className="t-cap mut mt4">
              {compare ? `${periodLabel(months[1]).split(' ')[0]} ${amount(trio[1]?.data?.refundedVatMinor ?? 0)}` : 'comes off what is owed'}
            </p>
          </div>
        </div>

        <DataTable
          caption={`Tax classes — ${monthName(period)}`}
          columns={[
            { key: 'cls', label: 'Tax class', width: 46 },
            { key: 'base', label: 'Base', width: 20 },
            { key: 'rate', label: 'Rate', width: 15 },
            { key: 'vat', label: 'VAT', width: 19 },
          ]}
          rows={rows}
          empty={
            <div className="emptystate">
              {isLoading ? (
                <p className="t-sec mut">Reading the month…</p>
              ) : isError ? (
                <p className="t-sec mut">It did not come back. Nothing has been changed.</p>
              ) : (
                <>
                  <p className="es-t">Nothing was billed in {monthName(period)}</p>
                  <p className="t-sec mut">There is no split to report.</p>
                </>
              )}
            </div>
          }
        />

        <div className="mt16">{rangeCard(false)}</div>

        <Card className="mt16">
          <p className="t-sec mut">
            The split is read from each service&rsquo;s tax class in the catalog — never a flat
            rate over everything — and a bill issued at zero is exempt whatever its lines are
            called. VAT is prorated to what was actually collected, so a half-paid invoice has not
            yet produced the whole of its tax.
          </p>
        </Card>
      </div>
    </div>
  );
}
